import { AfterViewInit, Component, inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  LayoutNames,
  NewWorkflowWidgetConfig,
  WorkflowPageConfig,
  findWidget,
} from '@finxone-platform/shared/sys-config-types';
import { CodeEditorType } from './code-editor.types';
import { CodeEditorComponent, CodeModel } from '@ngstack/code-editor';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { Subject } from 'rxjs';
import { ActiveConfigService } from '../../../libs/services/active-config/active-config.service';
import { ApiService } from '../../../libs/services/api-service/api.service';
import { PageTemplateService } from '../../../libs/services/page-template-service/page-template.service';
import { WorkflowConfigService } from '../../../libs/services/workflow-config/workflow-config.service';
import { WorkflowEditorService } from '../../../libs/services/workflow-editor/workflow-editor.service';
import { cloneDeep } from 'lodash';
import { PageNavigationMapService } from '../../../libs/services/page-navigation-map/page-navigation-map.service';
import { EditMockDataService } from '../../../libs/services/edit-mock-data/edit-mock-data.service';

@Component({
  standalone: false,
  selector: 'finxone-workflow-frontend-code-editor-popup',
  templateUrl: './code-editor-popup.component.html',
  styleUrls: ['./code-editor-popup.component.scss'],
})
export class CodeEditorPopupComponent implements AfterViewInit {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();

  public form: FormGroup;
  private type: CodeEditorType = CodeEditorType.PAGE;
  private projectId = '';

  public codeModel: CodeModel = {
    language: 'html',
    uri: 'main.json',
    value: '',
  };
  private widgetUniqueId: string;
  private templateId: string | undefined;
  private pageJsonToEdit: WorkflowPageConfig;

  private readonly pageNavigationMapService = inject(PageNavigationMapService);

  public ngxSmartModalService = inject(NgxSmartModalService);
  private workflowConfigService = inject(WorkflowConfigService);
  private workflowEditorService = inject(WorkflowEditorService);
  private apiService = inject(ApiService);
  private fb = inject(FormBuilder);
  private pageTemplateService = inject(PageTemplateService);
  private activeConfigService = inject(ActiveConfigService);
  private editMockDataService = inject(EditMockDataService);

  constructor() {
    this.form = this.fb.group({
      json: '',
    });
  }

  ngAfterViewInit(): void {
    this.ngxSmartModalService.getModal('codeEditorPopup').onOpen.subscribe(() => {
      setTimeout(() => {
        const modalData = this.ngxSmartModalService.getModalData('codeEditorPopup');

        if (modalData) {
          const {
            type,
            language,
            projectId,
            data,
            widgetUniqueId,
            templateId,
          }: {
            type: CodeEditorType;
            language: string;
            projectId?: string;
            data?: string;
            widgetUniqueId: string;
            templateId?: string;
          } = modalData as any;

          this.type = type;
          this.widgetUniqueId = widgetUniqueId;
          this.templateId = templateId;

          //Check data type and convert to string if object
          let editorValue = typeof data === 'object' ? JSON.stringify(data) : (data ?? '');

          switch (type) {
            case CodeEditorType.ALL:
              editorValue = JSON.stringify(this.workflowEditorService.workflowConfig);
              break;

            case CodeEditorType.WIDGET: {
              const widgets = this.workflowEditorService.getActivePage()
                ?.widgets as NewWorkflowWidgetConfig[];
              const widget = findWidget(widgetUniqueId, widgets);
              editorValue = JSON.stringify(widget);
              break;
            }

            case CodeEditorType.FOOTER_LAYOUT: {
              const widgets = this.workflowEditorService.getActivePage()
                ?.footerWidgets as NewWorkflowWidgetConfig[];
              const widget = findWidget(widgetUniqueId, widgets);
              editorValue = JSON.stringify(widget);
              break;
            }

            case CodeEditorType.PAGE: {
              const page = this.workflowEditorService.getActivePage();
              this.pageJsonToEdit = cloneDeep(page) as WorkflowPageConfig;
              editorValue = JSON.stringify(page);
              break;
            }

            case CodeEditorType.ZONE: {
              const zone = this.workflowEditorService.getActiveZone();
              editorValue = JSON.stringify(zone);
              break;
            }

            case CodeEditorType.MOCK_DATA: {
              editorValue = this.editMockDataService.getFilteredStateAsFormattedJson();
              break;
            }

            default:
              this.projectId = projectId ?? '';
              break;
          }

          this.codeModel = {
            ...this.codeModel,
            language: language,
            value: editorValue,
          };

          if (this.codeEditor?.editor) {
            this.codeEditor.editor.getModel('main.json').setValue(editorValue);
            this.codeEditor.formatDocument();
          }
        } else {
          console.error('Modal data is undefined');
        }
      }, 0);
    });
    this.ngxSmartModalService.getModal('codeEditorPopup').onClose.subscribe((modal) => {
      this.form.get('json')?.setValue('');
      // Removes existing modal data to ensure fresh input on the next open
      modal.removeData();
    });
  }

  public saveJson() {
    const rawJson = this.form.get('json')?.getRawValue();

    switch (this.type) {
      case CodeEditorType.ALL: {
        const parsedJson = JSON.parse(rawJson);
        this.workflowConfigService.updateConfig(() => parsedJson);
        break;
      }

      case CodeEditorType.WIDGET: {
        const parsedJson = JSON.parse(rawJson);
        this.workflowEditorService.overwriteWidget(parsedJson, this.widgetUniqueId);
        break;
      }

      case CodeEditorType.FOOTER_LAYOUT: {
        const parsedJson = JSON.parse(rawJson);
        this.workflowEditorService.overwriteWidget(
          parsedJson,
          this.widgetUniqueId,
          LayoutNames.FOOTER_LAYOUT,
        );
        break;
      }

      case CodeEditorType.PAGE: {
        const parsedJson = JSON.parse(rawJson);
        this.workflowEditorService.overwritePage(parsedJson);
        this.pageNavigationMapService.handlePageJsonUpdateAndUpdateMap(
          this.pageJsonToEdit,
          parsedJson,
          this.activeConfigService.activeState,
        );
        break;
      }

      case CodeEditorType.ZONE: {
        const parsedJson = JSON.parse(rawJson);
        this.workflowEditorService.overwriteZone(parsedJson);
        break;
      }

      case CodeEditorType.PROJECT: {
        const parsedJson = JSON.parse(rawJson);
        this.apiService.updateProjectConfig(this.projectId, parsedJson).subscribe();
        break;
      }

      case CodeEditorType.TEMPLATE: {
        const parsedJson = JSON.parse(rawJson);
        this.pageTemplateService
          .updateTemplatePageConfig(
            this.templateId ?? '',
            this.activeConfigService.activeState.activeDevice,
            parsedJson,
          )
          .subscribe();
        break;
      }

      case CodeEditorType.MOCK_DATA: {
        const saved = this.editMockDataService.saveEditedMockData(rawJson);
        if (!saved) return;
        break;
      }

      default: {
        this.ngxSmartModalService.setModalData(rawJson, 'codeEditorPopup');
        break;
      }
    }

    this.ngxSmartModalService.close('codeEditorPopup');
  }

  public closePopup(): void {
    this.ngxSmartModalService.close('codeEditorPopup');
  }

  private _codeEditor: CodeEditorComponent;

  @ViewChild(CodeEditorComponent, { static: false })
  set codeEditor(value: CodeEditorComponent) {
    this._codeEditor = value;
  }

  get codeEditor(): CodeEditorComponent {
    return this._codeEditor;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  change(event: any) {
    this.form.get('json')?.setValue(event);
  }
}
